/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Play } from 'lucide-react';

import { Text } from '@/shared/ui';

import { useLaunchMaterials } from '../../hooks/useLaunchMaterials';
import type { LaunchMaterial } from '../../types';

/** Same normalisation the backend applies to unit.typology. */
export function typologyKey(value?: string | null): string {
  return (value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

interface UnitMaterialsProps {
  agencyId: string;
  launchId: string;
  typology?: string | null;
  onOpen: (material: LaunchMaterial) => void;
}

/**
 * The materials tied to this unit's typology, inside the unit sheet.
 *
 * A horizontal strip, below the unit's details and above the reservation
 * form: it must be reachable without leaving the board, and must not
 * compete with the actions — the broker came here to hold a unit, and the
 * floor plan is support, not the point.
 */
export function UnitMaterials({ agencyId, launchId, typology, onOpen }: UnitMaterialsProps) {
  const { t } = useTranslation('features/launches');
  const { materials } = useLaunchMaterials(agencyId, launchId);
  const key = typologyKey(typology);

  const matching = useMemo(
    () => materials.filter((m) => key && (m.typology_keys ?? []).includes(key)),
    [materials, key],
  );

  if (matching.length === 0) return null;

  return (
    <div className="flex flex-col gap-[var(--space-2)]">
      <Text as="span" className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]">
        {t('materials.forThisTypology')}
      </Text>

      <div className="flex gap-[var(--space-2)] overflow-x-auto">
        {matching.map((material) => (
          <button
            key={material.material_id}
            type="button"
            onClick={() => onOpen(material)}
            className="flex min-h-[var(--space-12)] shrink-0 items-center gap-[var(--space-2)] rounded-(--radius-md) border border-[var(--border-subtle)] px-[var(--space-3)] text-left"
          >
            {material.source === 'embed'
              ? <Play size={14} color="var(--text-secondary)" />
              : <FileText size={14} color="var(--text-secondary)" />}
            <Text as="span" className="max-w-[18ch] truncate text-[length:var(--text-meta)] text-[var(--text-secondary)]">
              {material.title}
            </Text>
          </button>
        ))}
      </div>
    </div>
  );
}
